import { Capacitor, CapacitorHttp } from '@capacitor/core';
import {
  PushNotifications,
  PushNotificationSchema,
  ActionPerformed,
  PushNotificationToken,
} from '@capacitor/push-notifications';
import { doc, setDoc, deleteDoc, serverTimestamp } from 'firebase/firestore';
import { db, auth } from '@/firebase';
import { useToast } from '@/components/ui/use-toast';

const PUSH_WORKSPACE_KEY = 'camperros_push_workspace';

/**
 * Interfaz para el token del dispositivo
 */
export interface DeviceToken {
  token: string;
  platform: 'android' | 'web' | 'ios';
  userId: string;
  updatedAt: any;
  enabled: boolean;
  deviceId?: string;
}

/**
 * Genera un deviceId único para el dispositivo
 * Se persiste en localStorage para mantener consistencia entre sesiones
 * Este deviceId se usa como docId en deviceTokens/{deviceId}
 * Y se envía como senderDeviceId en pushEvents para filtrar exclusiones
 */
export function getOrCreateDeviceId(): string {
  const DEVICE_ID_KEY = 'camperros_device_id';
  let deviceId = localStorage.getItem(DEVICE_ID_KEY);

  if (!deviceId) {
    // Generar UUID v4 simple - persistente entre app reloads
    deviceId = `device_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    localStorage.setItem(DEVICE_ID_KEY, deviceId);
    console.log('[PUSH] Generated new persistent deviceId:', deviceId);
  }

  return deviceId;
}

/**
 * Obtiene permisos de notificaciones (crítico para Android 13+)
 */
export async function requestNotificationPermissions(): Promise<boolean> {
  try {
    console.log('[PUSH] Requesting notification permissions...');
    const result = await PushNotifications.checkPermissions();

    if (result.receive === 'prompt') {
      console.log('[PUSH] Permission prompt needed, requesting...');
      const permResult = await PushNotifications.requestPermissions();
      const hasPermission = permResult.receive === 'granted';

      if (hasPermission) {
        console.log('[PUSH] ✅ Notification permissions granted');
      } else {
        console.warn('[PUSH] ⚠️ Notification permissions denied');
      }

      return hasPermission;
    }

    const hasPermission = result.receive === 'granted';
    console.log('[PUSH] Permission check:', hasPermission ? 'granted' : 'denied/default');
    return hasPermission;
  } catch (error) {
    console.error('[PUSH] Error requesting permissions:', error);
    return false;
  }
}

/**
 * Registra el FCM token en Firestore
 * docId = deviceId (mismo que senderDeviceId en pushEvents)
 */
export async function registerFcmToken(token: string, workspaceId: string): Promise<string> {
  try {
    if (!auth.currentUser) {
      throw new Error('User not authenticated');
    }

    const userId = auth.currentUser.uid;
    const deviceId = getOrCreateDeviceId();

    // Limpiar token del workspace anterior si el dispositivo cambió de cuenta
    const prevWorkspaceId = localStorage.getItem(PUSH_WORKSPACE_KEY);
    if (prevWorkspaceId && prevWorkspaceId !== workspaceId) {
      try {
        const oldRef = doc(db, 'workspaces', prevWorkspaceId, 'deviceTokens', deviceId);
        await deleteDoc(oldRef);
        console.log('[PUSH] Cleaned up token from previous workspace:', prevWorkspaceId);
      } catch (e) {
        console.warn('[PUSH] Could not clean old workspace token:', e);
      }
    }
    localStorage.setItem(PUSH_WORKSPACE_KEY, workspaceId);

    const deviceTokenRef = doc(
      db,
      'workspaces',
      workspaceId,
      'deviceTokens',
      deviceId
    );

    const tokenData: DeviceToken = {
      token,
      platform: 'android',
      userId,
      updatedAt: serverTimestamp(),
      enabled: true,
      deviceId,
    };

    // Guardar con docId = deviceId para que Cloud Function pueda filtrar por doc.id
    await setDoc(deviceTokenRef, tokenData, { merge: true });

    console.log('[PUSH] ✅ FCM token registered in Firestore:', {
      workspaceId,
      deviceId,
      userId,
      tokenPrefix: token.substring(0, 20) + '...',
    });

    return deviceId;
  } catch (error) {
    console.error('[PUSH] Error registering FCM token:', error);
    throw error;
  }
}

/**
 * Elimina un token inválido de Firestore
 * Se llama cuando FCM reporta "registration-token-not-registered"
 */
export async function removeInvalidToken(
  deviceId: string,
  workspaceId: string
): Promise<void> {
  try {
    const deviceTokenRef = doc(
      db,
      'workspaces',
      workspaceId,
      'deviceTokens',
      deviceId
    );

    await deleteDoc(deviceTokenRef);

    console.log('[PUSH] ✅ Invalid token removed from Firestore:', deviceId);
  } catch (error) {
    console.error('[PUSH] Error removing invalid token:', error);
    throw error;
  }
}

/**
 * Inicializa el sistema de push notifications
 * Solo se ejecuta en plataforma nativa (Android/iOS)
 */
export async function initializePushNotifications(
  workspaceId: string,
  showToast?: (message: string) => void
): Promise<void> {
  // Solo en plataforma nativa
  if (!Capacitor.isNativePlatform()) {
    console.log('[PUSH] Not a native platform, skipping push initialization');
    return;
  }

  try {
    console.log('[PUSH] Initializing push notifications...');

    // 1. Solicitar permisos (crítico para Android 13+)
    const hasPermission = await requestNotificationPermissions();

    if (!hasPermission) {
      console.warn('[PUSH] ⚠️ User denied notification permissions');
      if (showToast) {
        showToast('Notificaciones desactivadas - enable them in settings');
      }
      return;
    }

    // 2. Registrar para push
    await PushNotifications.register();
    console.log('[PUSH] ✅ Registered for push notifications');

    // 3. Listener: nuevo token registrado
    PushNotifications.addListener('registration', async (token: PushNotificationToken) => {
      console.log('[PUSH] 📱 FCM Token received:', token.value.substring(0, 30) + '...');

      try {
        await registerFcmToken(token.value, workspaceId);
      } catch (error) {
        console.error('[PUSH] Error registering token:', error);
        if (showToast) {
          showToast('Error al registrar notificaciones');
        }
      }
    });

    // 4. Listener: error en el registro
    PushNotifications.addListener('registrationError', (error: any) => {
      console.error('[PUSH] Registration error:', error);
      if (showToast) {
        showToast('Error en registro de notificaciones');
      }
    });

    // 5. Listener: notificación recibida (app en foreground)
    PushNotifications.addListener(
      'pushNotificationReceived',
      async (notification: PushNotificationSchema) => {
        console.log('[PUSH] 🔔 Notification received (foreground):', {
          title: notification.title,
          body: notification.body,
          data: notification.data,
        });

        // Mostrar toast en foreground
        if (notification.body && showToast) {
          showToast(notification.body);
        }

        // Aquí puedes actualizar la UI si es necesario
        // Por ejemplo, refrescar la lista de avisos
      }
    );

    // 6. Listener: usuario toca la notificación
    PushNotifications.addListener(
      'pushNotificationActionPerformed',
      async (action: ActionPerformed) => {
        const { notification } = action;

        console.log('[PUSH] 👆 Notification tapped:', {
          title: notification.title,
          data: notification.data,
        });

        // Navegar a la pantalla de avisos
        const route = notification.data?.route || 'avisos';
        console.log('[PUSH] Navigating to:', route);

        // Emitir evento personalizado para que AppCore navege
        window.dispatchEvent(
          new CustomEvent('pushNotificationTapped', {
            detail: {
              route,
              data: notification.data,
            },
          })
        );
      }
    );

    console.log('[PUSH] ✅ All listeners registered');
  } catch (error) {
    console.error('[PUSH] Error initializing push notifications:', error);
    if (showToast) {
      showToast('Error al inicializar notificaciones');
    }
  }
}

/**
 * Desregistra todos los listeners (cleanup)
 */
export async function unregisterPushListeners(): Promise<void> {
  try {
    PushNotifications.removeAllListeners();
    console.log('[PUSH] ✅ All listeners removed');
  } catch (error) {
    console.error('[PUSH] Error removing listeners:', error);
  }
}

/**
 * Limpia el token FCM del workspace actual al cerrar sesión.
 * Lee el workspaceId desde localStorage para no necesitar pasarlo como parámetro.
 */
export async function cleanupPushOnLogout(): Promise<void> {
  if (!Capacitor.isNativePlatform()) return;
  try {
    const workspaceId = localStorage.getItem(PUSH_WORKSPACE_KEY);
    if (!workspaceId) return;
    const deviceId = getOrCreateDeviceId();
    const ref = doc(db, 'workspaces', workspaceId, 'deviceTokens', deviceId);
    await deleteDoc(ref);
    localStorage.removeItem(PUSH_WORKSPACE_KEY);
    PushNotifications.removeAllListeners();
    console.log('[PUSH] ✅ Cleanup on logout done');
  } catch (e) {
    console.warn('[PUSH] Cleanup error on logout:', e);
  }
}

/**
 * Habilita/deshabilita las notificaciones del dispositivo en Firestore
 */
export async function setDeviceTokenEnabled(
  deviceId: string,
  workspaceId: string,
  enabled: boolean
): Promise<void> {
  try {
    const deviceTokenRef = doc(
      db,
      'workspaces',
      workspaceId,
      'deviceTokens',
      deviceId
    );

    await setDoc(deviceTokenRef, { enabled }, { merge: true });

    console.log(`[PUSH] ✅ Device token enabled set to ${enabled}`);
  } catch (error) {
    console.error('[PUSH] Error setting device token enabled:', error);
    throw error;
  }
}
